import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReferralCard, { shareInvite } from "@/components/ReferralCard";
import InviteLeaderboard from "@/components/InviteLeaderboard";
import { TopUpCard, DonateButton } from "@/components/TopUpDonate";
import { Map, Store, Repeat, Wallet, User, Search, Shield, Share2, ArrowRight, Users, Sparkles, Heart, Globe, Check, BookOpen } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { useT } from "@/i18n";
import LanguageSwitcher from "@/components/LanguageSwitcher";

export default function Home() {
  const { user } = useAuth();
  const { settings } = useTheme();
  const { t } = useT();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const appName = settings?.app_name || "Deuro";
  const [params] = useSearchParams();

  useEffect(() => {
    const ref = params.get("ref");
    if (ref) localStorage.setItem("deuro_ref", ref);
  }, [params]);

  const share = async () => {
    const onCopied = () => { setCopied(true); setTimeout(() => setCopied(false), 2500); };
    if (user?.username) return shareInvite(user.username, t, onCopied);
    const url = window.location.origin;
    try {
      if (navigator.share) { await navigator.share({ title: appName, text: t("home_share_text"), url }); return; }
      await navigator.clipboard.writeText(`${t("home_share_text")} ${url}`);
      onCopied();
    } catch { /* user cancelled */ }
  };

  const links = [
    { to: "/mappa", icon: Map, k: "nav_map", d: "home_go_map", feature: "map", testid: "home-go-map" },
    { to: "/mercato", icon: Store, k: "nav_market", d: "home_go_market", feature: "market", testid: "home-go-market" },
    { to: "/scambi", icon: Repeat, k: "nav_trades", d: "home_go_trades", feature: "trades", testid: "home-go-trades" },
    { to: "/wallet", icon: Wallet, k: "nav_wallet", d: "home_go_wallet", testid: "home-go-wallet" },
    { to: "/profilo", icon: User, k: "nav_profile", d: "home_go_profile", testid: "home-go-profile" },
    { to: "/cerca", icon: Search, k: "nav_search", d: "home_go_search", testid: "home-go-search" },
  ].filter((l) => !l.feature || settings?.features?.[l.feature] !== false);
  if (user?.role === "admin") links.push({ to: "/admin", icon: Shield, k: "nav_admin", d: "home_go_admin", testid: "home-go-admin" });

  return (
    <div className="pb-10" data-testid="home-page">
      {!user && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-192.png" alt={appName} width="36" height="36" className="w-9 h-9 rounded-xl object-cover" />
            <span className="font-head font-extrabold text-xl tracking-tight">{appName}</span>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <button onClick={() => navigate("/accedi")} data-testid="home-login-btn" className="px-4 py-2 rounded-full text-sm font-semibold text-[#1e3326] hover:bg-[#f4f0e8]">{t("home_login_cta")}</button>
            <button onClick={() => navigate("/registrati")} data-testid="home-register-btn" className="px-4 py-2 rounded-full text-sm font-semibold bg-[#1e3326] text-white hover:bg-[#284635]">{t("home_register_cta")}</button>
          </div>
        </div>
      )}

      {/* Hero */}
      <section className="relative overflow-hidden mx-4 sm:mx-6 mt-4 rounded-3xl grain" style={{ background: "var(--primary)" }} data-testid="home-hero">
        <img src="/hero-home.webp" alt="" width="1264" height="848" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(var(--primary-rgb),0.92) 0%, rgba(var(--primary-rgb),0.55) 60%, rgba(var(--primary-rgb),0.2) 100%)" }} />
        <div className="relative z-10 px-6 sm:px-12 py-14 sm:py-20 max-w-3xl">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#f59e0b]">
            <Sparkles className="w-3.5 h-3.5" /> {t("home_kicker")}
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#fdfbf7] leading-[1.05] tracking-tight mt-4">
            {appName}: {t("home_title")}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.6 }}
            className="mt-6 text-base md:text-lg text-[#dfe6e0] leading-relaxed">{t("home_lead")}</motion.p>
          <div className="mt-8 flex flex-wrap gap-3">
            {user ? (
              <button onClick={() => navigate("/cerca")} data-testid="hero-explore-btn" className="flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#d97706] text-white font-semibold hover:bg-[#f59e0b] transition-all">
                {t("home_explore")} <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button onClick={() => navigate("/registrati")} data-testid="hero-register-btn" className="flex items-center gap-2 px-6 py-3.5 rounded-xl bg-[#d97706] text-white font-semibold hover:bg-[#f59e0b] transition-all">
                {t("home_register_cta")} <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button onClick={share} data-testid="hero-share-btn" className="flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white/10 text-white font-semibold border border-white/20 hover:bg-white/20 transition-all">
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />} {t("home_grow_cta")}
            </button>
          </div>
        </div>
      </section>

      {/* Quick links */}
      {user && (
        <section className="px-4 sm:px-6 mt-8" data-testid="home-quick-links">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {links.map((l, i) => (
              <motion.button key={l.to} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 * i }}
                onClick={() => navigate(l.to)} data-testid={l.testid}
                className="text-left bg-white rounded-2xl border border-[#e5dfd3] card-shadow p-4 hover:-translate-y-0.5 hover:border-[#d97706] transition-all group">
                <div className="w-10 h-10 rounded-xl bg-[#f4f0e8] group-hover:bg-[#fef3c7] flex items-center justify-center text-[#1e3326] group-hover:text-[#d97706] transition-colors">
                  <l.icon className="w-5 h-5" />
                </div>
                <p className="font-head font-bold mt-3">{t(l.k)}</p>
                <p className="text-xs text-[#5f6b62] mt-0.5 leading-snug">{t(l.d)}</p>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      {user && <section className="px-4 sm:px-6 mt-8"><TopUpCard compact /></section>}

      {/* What & how */}
      <section className="px-4 sm:px-6 mt-12 grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white rounded-3xl border border-[#e5dfd3] card-shadow p-7 sm:p-9">
          <h2 className="font-head text-base md:text-lg font-bold text-[#d97706] uppercase tracking-wider">{t("home_what_t")}</h2>
          <p className="mt-3 text-base sm:text-lg leading-relaxed text-[#1b241e]">{t("home_what_p")}</p>
          <h2 className="font-head text-base md:text-lg font-bold text-[#d97706] uppercase tracking-wider mt-8">{t("home_how_t")}</h2>
          <ol className="mt-3 space-y-3">
            {["home_how_1", "home_how_2", "home_how_3"].map((k, i) => (
              <li key={k} className="flex gap-3 text-[#1b241e]">
                <span className="w-7 h-7 shrink-0 rounded-full bg-[#1e3326] text-white font-mono text-sm flex items-center justify-center">{i + 1}</span>
                <span className="leading-relaxed">{t(k)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="rounded-3xl p-7 grain flex-1" style={{ background: "var(--primary)", color: "var(--on-primary)" }}>
            <Heart className="w-6 h-6 text-[#f59e0b]" />
            <h2 className="font-head text-base md:text-lg font-bold mt-3">{t("home_phil_t")}</h2>
            <p className="mt-2 text-sm leading-relaxed text-[#dfe6e0]">{t("home_phil_p")}</p>
          </div>
          <div className="rounded-3xl p-6 bg-[#fef3c7] border border-[#f3d98a] flex gap-3">
            <Globe className="w-5 h-5 text-[#92600a] shrink-0 mt-0.5" />
            <p className="text-sm text-[#92600a] leading-relaxed">{t("home_web_note")}</p>
          </div>
        </div>
      </section>

      {/* Approfondimenti */}
      <section className="px-4 sm:px-6 mt-12" data-testid="home-deep-dive">
        <div className="bg-white rounded-3xl border border-[#e5dfd3] card-shadow p-7 sm:p-9">
          <h2 className="font-head text-base md:text-lg font-bold text-[#d97706] uppercase tracking-wider flex items-center gap-2"><BookOpen className="w-5 h-5" /> {t("home_deep_t")}</h2>
          <div className="mt-4 space-y-4 text-base leading-relaxed text-[#1b241e]">
            {["home_deep_1", "home_deep_2", "home_deep_3", "home_deep_4", "home_deep_5", "home_deep_6"].map((k) => (
              <p key={k} data-testid={k}>{t(k)}</p>
            ))}
            <p className="font-semibold text-[#1e3326] border-l-4 border-[#d97706] pl-4" data-testid="home_deep_7">{t("home_deep_7")}</p>
          </div>
        </div>
      </section>

      {/* Grow the network */}
      <section className="px-4 sm:px-6 mt-12" data-testid="home-grow">
        <div className="relative overflow-hidden rounded-3xl border-2 border-[#d97706] bg-white p-7 sm:p-10">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(217,119,6,0.18), transparent 70%)" }} />
          <div className="relative z-10 grid lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-2">
              <h2 className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#fef3c7] text-[#92600a] text-xs font-bold uppercase tracking-wider"><Users className="w-3.5 h-3.5" /> {t("home_grow_t")}</h2>
              <p className="mt-4 text-base sm:text-lg leading-relaxed text-[#1b241e]">{t("home_grow_p1")}</p>
              <p className="mt-3 text-base sm:text-lg leading-relaxed font-semibold text-[#1e3326]">{t("home_grow_p2")}</p>
            </div>
            <div className="flex flex-col gap-3">
              {user && <ReferralCard compact />}
              <button onClick={share} data-testid="grow-share-btn" className="flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-[#d97706] text-white font-bold text-lg hover:bg-[#c26806] active:scale-[0.99] transition-all">
                {copied ? <Check className="w-5 h-5" /> : <Share2 className="w-5 h-5" />} {t("home_grow_cta")}
              </button>
              {copied && <p className="text-sm text-[#059669] text-center" data-testid="share-copied">{t("home_copied")}</p>}
              {user && <DonateButton className="w-full" />}
              {!user && (
                <button onClick={() => navigate("/registrati")} data-testid="grow-register-btn" className="px-6 py-3 rounded-xl border-2 border-[#1e3326] text-[#1e3326] font-semibold hover:bg-[#f4f0e8]">{t("home_register_cta")}</button>
              )}
            </div>
          </div>
        </div>
      </section>

      {user && <InviteLeaderboard />}
    </div>
  );
}
